/**
 * V70 엔글 마블 V3 — 3D CSS 주사위
 *
 * 중요 (사용자 지시 §3.1):
 *   - 애니메이션으로 표시된 최종 눈금과 실제 게임 결과가 반드시 일치
 *   - 시각적 주사위가 별도로 난수를 생성하면 안 됨
 *   - prop `value` 가 1~6 중 결정된 결과 → 그 면에서 정지
 *   - prefers-reduced-motion 환경 → 즉시 결과 표시
 */
import React, { useEffect, useRef, useState } from 'react';
import './Dice3D.css';

export interface Dice3DProps {
  /** 결과 눈금 1~6. null 이면 대기 상태 (?). */
  value: number | null;
  /** 굴림 중 (spinning=true 면 랜덤 회전) */
  spinning: boolean;
  /** 크기 (default 72) */
  size?: number;
}

/**
 * 각 면 → 회전 각도 (3D 큐브 기준).
 * 큐브의 face 가 카메라를 향하도록 X/Y 회전 매핑.
 */
const FACE_ROTATION: Record<number, { x: number; y: number }> = {
  1: { x: 0,   y: 0 },      // front
  2: { x: 0,   y: 180 },    // back
  3: { x: 0,   y: -90 },    // right
  4: { x: 0,   y: 90 },     // left
  5: { x: -90, y: 0 },      // top
  6: { x: 90,  y: 0 },      // bottom
};

const PIP_POSITIONS: Record<number, Array<[number, number]>> = {
  1: [[2, 2]],
  2: [[1, 1], [3, 3]],
  3: [[1, 1], [2, 2], [3, 3]],
  4: [[1, 1], [1, 3], [3, 1], [3, 3]],
  5: [[1, 1], [1, 3], [2, 2], [3, 1], [3, 3]],
  6: [[1, 1], [1, 3], [2, 1], [2, 3], [3, 1], [3, 3]],
};

export function Dice3D({ value, spinning, size = 72 }: Dice3DProps) {
  const [spinOffsetDeg, setSpinOffsetDeg] = useState({ x: 0, y: 0 });
  const spinFrameRef = useRef<number | null>(null);

  // 굴림 중 → 랜덤 회전 (단, 결과값 자체는 prop 으로만 결정)
  useEffect(() => {
    if (spinning) {
      let offset = { x: 0, y: 0 };
      const tick = () => {
        offset = { x: offset.x + 90 + Math.random() * 180, y: offset.y + 90 + Math.random() * 180 };
        setSpinOffsetDeg(offset);
        spinFrameRef.current = window.setTimeout(tick, 150);
      };
      tick();
      return () => {
        if (spinFrameRef.current != null) window.clearTimeout(spinFrameRef.current);
      };
    } else {
      // 결과 멈춤
      setSpinOffsetDeg({ x: 0, y: 0 });
    }
  }, [spinning]);

  // 결과 회전 (value prop 기준)
  const targetRot = value != null && FACE_ROTATION[value]
    ? FACE_ROTATION[value]
    : { x: -15, y: 20 };  // 대기 시 살짝 틀어서 입체감

  const finalX = spinning ? spinOffsetDeg.x : targetRot.x;
  const finalY = spinning ? spinOffsetDeg.y : targetRot.y;

  const faces: Array<[number, string]> = [
    [1, 'front'], [2, 'back'], [3, 'right'], [4, 'left'], [5, 'top'], [6, 'bottom'],
  ];

  return (
    <div className={`marble-dice-3d-stage ${spinning ? 'spinning' : ''}`} style={{ width: size, height: size }}>
      <div
        className="marble-dice-3d-cube"
        style={{
          transform: `rotateX(${finalX}deg) rotateY(${finalY}deg)`,
          width: size, height: size,
        }}
      >
        {faces.map(([faceValue, faceName]) => (
          <div
            key={faceName}
            className={`marble-dice-3d-face face-${faceName}`}
            style={{
              width: size, height: size,
              transform: faceTransform(faceName, size),
            }}
          >
            {value == null && faceName === 'front' ? (
              <div className="marble-dice-3d-waiting">?</div>
            ) : (
              <DicePips value={faceValue} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function faceTransform(face: string, size: number): string {
  const half = size / 2;
  switch (face) {
    case 'front':  return `translateZ(${half}px)`;
    case 'back':   return `rotateY(180deg) translateZ(${half}px)`;
    case 'right':  return `rotateY(90deg) translateZ(${half}px)`;
    case 'left':   return `rotateY(-90deg) translateZ(${half}px)`;
    case 'top':    return `rotateX(90deg) translateZ(${half}px)`;
    case 'bottom': return `rotateX(-90deg) translateZ(${half}px)`;
    default:       return '';
  }
}

function DicePips({ value }: { value: number }) {
  const pips = PIP_POSITIONS[value] ?? [];
  return (
    <div className="marble-dice-3d-pip-grid">
      {pips.map(([row, col], i) => (
        <div key={i} className="marble-dice-3d-pip" style={{ gridRow: row, gridColumn: col }} />
      ))}
    </div>
  );
}

export default Dice3D;
